import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SearchIcon from "@mui/icons-material/Search";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import { styled, SxProps } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import { debounce } from "lodash";
import PermissionfilterParameters from "permissions/_api/queries/filtering/permission-filter-parameters";
import PermissionorderParameters from "permissions/_api/queries/ordering/permission-order-params";
import PermissionTemplateQuery from "permissions/_api/request-contracts/permission-template-query";
import {
  getErrorLoadingPermissionTemplatesByQuery,
  getIsLoadingPermissionTemplatesByQuery,
  getPermissionTemplatesByQuery,
  getPermissionTemplatesQueryByInterface,
  populatePermissionTemplatesByQuery,
  updatePermissionTemplateInterfaceQuery,
} from "permissions/_stores/permission-templates-redux-slice";
import PermissionTemplate from "permissions/templates/permission-template";
import React, { ReactNode, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useSession } from "users/__session/session-context";

const TemplatesMenu = styled(Menu)(({ theme }) => ({
  "& .MuiPaper-root": {
    border: `1px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(0.5),
  },
}));
const MenuContainer = styled("div")(({ theme }) => ({
  minWidth: "300px",
  padding: theme.spacing(0.75, 1.5),
}));
const Header = styled("div")(() => ({
  alignItems: "center",
  display: "grid",
  gap: 1,
  gridTemplateColumns: "1fr auto",
}));
const MenuContentContainer = styled("div")(({ theme }) => ({
  marginTop: theme.spacing(1),
  maxHeight: "500px",
  maxWidth: "400px",
  overflowY: "auto",
}));

export type ApplyTemplateMenuButtonProps = {
  key?: string;
  className?: string;
  disabled?: boolean;
  sx?: SxProps;
  onApply?: (template: PermissionTemplate) => void;
};

export default function ApplyTemplateMenuButton(props: Readonly<ApplyTemplateMenuButtonProps>): ReactNode {
  const { key, className, disabled, sx, onApply } = props;

  const interfaceName = `apply-template-menu-${key}`;
  const permissionTemplatesQuery = useSelector(getPermissionTemplatesQueryByInterface(interfaceName));
  const permissionTemplatesResponse = useSelector(getPermissionTemplatesByQuery(permissionTemplatesQuery));
  const isLoadingPermissionTemplates = useSelector(getIsLoadingPermissionTemplatesByQuery(permissionTemplatesQuery));
  const permissionTemplatesLoadErrors = useSelector(getErrorLoadingPermissionTemplatesByQuery(permissionTemplatesQuery));

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [search, setSearch] = useState<string>("");

  const session = useSession();
  const dispatch = useAttorneyHubDispatch();
  const debouncedSearch = debounce((value) => setSearch(value), 300);

  const open = Boolean(anchorEl);

  // TODO: Have what the below useEffect does when the auto-complete is opened and when search text changes.

  useEffect(() => {
    const newTemplatesQuery = new PermissionTemplateQuery({
      filterParameters: Object.assign(new PermissionfilterParameters({}), { name: search.trim() }),
      orderParameters: new PermissionorderParameters({}),
      paginationParameters: PaginationParameters.create({ pageIndex: 0, pageSize: 25 }),
    });

    if (newTemplatesQuery.isEqualTo(permissionTemplatesQuery)) return;

    if (!isLoadingPermissionTemplates && !permissionTemplatesLoadErrors) {
      dispatch(
        updatePermissionTemplateInterfaceQuery({
          interfaceName,
          query: newTemplatesQuery
        })
      );

      dispatch(
        populatePermissionTemplatesByQuery({
          session,
          query: newTemplatesQuery,
        })
      );
    }
  }, [search, dispatch, isLoadingPermissionTemplates, permissionTemplatesLoadErrors, interfaceName, permissionTemplatesQuery, session]);


  function handleMenuOpen(event: React.MouseEvent<HTMLElement>) {
    setAnchorEl(event.currentTarget);
  }

  function handleClose() {
    setAnchorEl(null);
  }

  function handleApplyTemplate(template: PermissionTemplate) {
    onApply?.(template);
    handleClose();
  }

  return (
    <Box className={className} sx={sx}>
      <Button
        startIcon={<AutoFixHighIcon />}
        endIcon={<KeyboardArrowDownIcon />}
        variant="contained"
        disabled={disabled || open}
        color="secondary"
        disableElevation
        onClick={handleMenuOpen}
      >
        Apply Template
      </Button>
      <TemplatesMenu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        elevation={0}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        marginThreshold={0}
      >
        <MenuContainer>
          <Header>
            <TextField
              placeholder="Search templates"
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: search ? (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setSearch("")}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                },
              }}
              size="small"
              fullWidth
              variant="standard"
              value={search}
              onChange={(e) => debouncedSearch(e.target.value)}
            />
          </Header>
          <MenuContentContainer>
            <List disablePadding dense>
              {isLoadingPermissionTemplates && (
                <ListItem disableGutters>
                  <ListItemText primary="Loading templates..." />
                </ListItem>
              )}
              {!isLoadingPermissionTemplates && permissionTemplatesResponse?.data.length === 0 && (
                <ListItem disableGutters>
                  <ListItemText primary="No templates available" />
                </ListItem>
              )}
              {permissionTemplatesLoadErrors && (
                <ListItem disableGutters>
                  <ListItemText primary="Error loading templates" />
                </ListItem>
              )}
              {!isLoadingPermissionTemplates &&
                (permissionTemplatesResponse?.data ?? []).map((template) => (
                  <ListItem
                    key={template.id.value}
                    disableGutters
                    sx={{ pr: 10 }}
                    secondaryAction={
                      <Button size="small" onClick={() => handleApplyTemplate(template)}>
                        Apply
                      </Button>
                    }
                  >
                    <ListItemText primary={template.name} secondary={template.description} />
                  </ListItem>
                ))}
            </List>
          </MenuContentContainer>
        </MenuContainer>
      </TemplatesMenu>
    </Box>
  );
}